<!-- src/lib/components/PrioritySignal.svelte -->
<!--
  Linear-style 4-bar cellular signal icon for card priority. Replaces
  the colored dot we were using before. Bars fill according to the
  priority level, giving a clean left-to-right visual ramp that reads
  at a glance from six feet away.

    none     → 4 empty bars
    low      → 1 bar filled
    medium   → 2 bars filled
    high     → 3 bars filled
    urgent   → 4 bars filled + red tint

  Click fires onCycle so the board row can cycle priority inline
  without opening the drawer.
-->
<script lang="ts">
  type Priority = 'none' | 'low' | 'medium' | 'high' | 'urgent';

  interface Props {
    priority: Priority | string;
    size?: number;
    /** Optional inline-edit handler: fires on click with the next level. */
    onCycle?: (next: Priority) => void;
  }
  let { priority, size = 14, onCycle }: Props = $props();

  const LEVELS: Record<string, number> = {
    none: 0, low: 1, medium: 2, high: 3, urgent: 4,
  };
  const level = $derived(LEVELS[priority] ?? 0);
  const isUrgent = $derived(priority === 'urgent');

  // Color ramp. Urgent is red, others fill with near-white.
  const fillColor = $derived(
    isUrgent ? 'oklch(0.68 0.24 25)' : 'oklch(0.88 0.03 310 / 0.90)'
  );
  const emptyColor = 'oklch(0.45 0.03 310 / 0.30)';

  const order: Priority[] = ['none', 'low', 'medium', 'high', 'urgent'];
  function cycleNext(): Priority {
    const i = order.indexOf(priority as Priority);
    return order[(i + 1) % order.length];
  }

  function handleClick(e: MouseEvent) {
    if (!onCycle) return;
    e.stopPropagation();
    onCycle(cycleNext());
  }
</script>

{#if onCycle}
  <button
    type="button"
    class="signal-btn"
    onclick={handleClick}
    title="Priority: {priority}"
    aria-label="Priority: {priority}"
    style="--size: {size}px;"
  >
    <svg width={size} height={size} viewBox="0 0 14 14" fill="none">
      <rect x="1"    y="9" width="2.2" height="4"  rx="0.6" fill={level >= 1 ? fillColor : emptyColor}/>
      <rect x="4.4"  y="7" width="2.2" height="6"  rx="0.6" fill={level >= 2 ? fillColor : emptyColor}/>
      <rect x="7.8"  y="5" width="2.2" height="8"  rx="0.6" fill={level >= 3 ? fillColor : emptyColor}/>
      <rect x="11.2" y="3" width="2.2" height="10" rx="0.6" fill={level >= 4 ? fillColor : emptyColor}/>
    </svg>
  </button>
{:else}
  <span class="signal" title="Priority: {priority}" style="--size: {size}px;">
    <svg width={size} height={size} viewBox="0 0 14 14" fill="none">
      <rect x="1"    y="9" width="2.2" height="4"  rx="0.6" fill={level >= 1 ? fillColor : emptyColor}/>
      <rect x="4.4"  y="7" width="2.2" height="6"  rx="0.6" fill={level >= 2 ? fillColor : emptyColor}/>
      <rect x="7.8"  y="5" width="2.2" height="8"  rx="0.6" fill={level >= 3 ? fillColor : emptyColor}/>
      <rect x="11.2" y="3" width="2.2" height="10" rx="0.6" fill={level >= 4 ? fillColor : emptyColor}/>
    </svg>
  </span>
{/if}

<style>
  .signal,
  .signal-btn {
    display: inline-flex;
    width:  var(--size);
    height: var(--size);
    flex-shrink: 0;
    vertical-align: middle;
  }
  .signal-btn {
    background: transparent;
    border: none;
    padding: 0;
    cursor: pointer;
    border-radius: 2px;
    transition: background 100ms ease;
  }
  .signal-btn:hover {
    background: oklch(0.72 0.28 310 / 0.10);
  }
</style>
